import { Component } from '@theme/component';
import { lockScroll, unlockScroll, prefersReducedMotion } from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';

/**
 * NCC: <ncc-lightbox> - the product gallery's "Lightbox" media viewer (blocks/_product-media-gallery.liquid,
 * setting `zoom_style`). One image at a time on a dark backdrop with previous / next arrows, a counter and a
 * thumbnail strip; images crossfade, arrow keys and swipes navigate, Escape or a click on the backdrop closes.
 * It answers the same `open(index, event)` call and the same `#zoom-dialog-<block id>` id as Horizon's
 * zoom-dialog, so the gallery's click / zoom-button wiring and media-gallery.js's slide sync are unchanged.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} media - one item per gallery media, in gallery order
 * @property {HTMLElement} thumbnails
 * @property {HTMLElement} counter
 * @property {HTMLElement} stage
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends Component<Refs>
 */
class NccLightbox extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails', 'counter', 'stage'];

  #index = 0;
  #swipeStart = /** @type {{ x: number, y: number } | null} */ (null);

  connectedCallback() {
    super.connectedCallback();
    const { dialog, stage } = this.refs;
    dialog.addEventListener('cancel', this.#onCancel);
    dialog.addEventListener('click', this.#onBackdropClick);
    stage.addEventListener('pointerdown', this.#onPointerDown, { passive: true });
    stage.addEventListener('pointerup', this.#onPointerUp, { passive: true });
    stage.addEventListener('pointercancel', this.#onPointerCancel, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { dialog, stage } = this.refs;
    dialog.removeEventListener('cancel', this.#onCancel);
    dialog.removeEventListener('click', this.#onBackdropClick);
    stage.removeEventListener('pointerdown', this.#onPointerDown);
    stage.removeEventListener('pointerup', this.#onPointerUp);
    stage.removeEventListener('pointercancel', this.#onPointerCancel);
    if (dialog.open) unlockScroll(dialog);
  }

  get count() {
    return this.refs.media.length;
  }

  /**
   * Opens the lightbox on a media item (the gallery's click and zoom-button target).
   * @param {number | string} index
   * @param {Event} [event]
   */
  open(index, event) {
    event?.preventDefault();
    const { dialog } = this.refs;
    this.#show(Number(index) || 0, { animate: false });
    if (dialog.open) return;
    lockScroll(dialog);
    dialog.showModal();
    this.#preloadAround(this.#index);
    dialog.focus({ preventScroll: true }); // the dialog owns the keyboard (tabindex -1); no focus ring on a control
  }

  close = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    if (!prefersReducedMotion()) {
      dialog.classList.add('ncc-lightbox__dialog--closing');
      await new Promise((resolve) => setTimeout(resolve, 220));
      dialog.classList.remove('ncc-lightbox__dialog--closing');
    }
    unlockScroll(dialog);
    dialog.close();
  };

  next = () => this.#show((this.#index + 1) % this.count);

  previous = () => this.#show((this.#index - 1 + this.count) % this.count);

  /** @param {number | string} index */
  handleThumbnailClick(index) {
    this.#show(Number(index));
  }

  /** @param {KeyboardEvent} event */
  handleKeyDown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    }
  }

  /**
   * Horizon's media-gallery calls this to warm the zoomed image on hover; here it just kicks the lazy load.
   * @param {HTMLElement} item
   */
  loadHighResolutionImage(item) {
    const img = item?.querySelector('img');
    if (img instanceof HTMLImageElement) img.loading = 'eager';
  }

  /**
   * @param {number} index
   * @param {{ animate?: boolean }} [options]
   */
  #show(index, { animate = true } = {}) {
    const { media, thumbnails, counter } = this.refs;
    if (!media.length) return;
    const target = ((index % media.length) + media.length) % media.length;
    const changed = target !== this.#index;
    this.#index = target;
    media.forEach((item, i) => {
      const active = i === target;
      item.classList.toggle('ncc-lightbox__item--active', active);
      item.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (!animate) item.classList.toggle('ncc-lightbox__item--instant', true);
    });
    if (!animate) requestAnimationFrame(() => media.forEach((item) => item.classList.remove('ncc-lightbox__item--instant')));
    Array.from(thumbnails.children).forEach((thumb, i) => {
      thumb.setAttribute('aria-current', i === target ? 'true' : 'false');
      if (i === target && thumb instanceof HTMLElement) thumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: animate && !prefersReducedMotion() ? 'smooth' : 'instant' });
    });
    counter.textContent = `${target + 1} / ${media.length}`;
    this.#preloadAround(target);
    if (changed) this.dispatchEvent(new ZoomMediaSelectedEvent(target));
  }

  /** @param {number} index */
  #preloadAround(index) {
    const { media } = this.refs;
    for (const i of [index, index + 1, index - 1]) {
      const item = media[((i % media.length) + media.length) % media.length];
      if (item) this.loadHighResolutionImage(item);
    }
  }

  #onCancel = (/** @type {Event} */ event) => {
    event.preventDefault();
    this.close();
  };

  #onBackdropClick = (/** @type {MouseEvent} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    // anything that is not a control or the picture itself counts as the backdrop
    if (target.closest('button, img, video, iframe, model-viewer, .ncc-lightbox__thumbs')) return;
    this.close();
  };

  #onPointerDown = (/** @type {PointerEvent} */ event) => {
    if (event.pointerType === 'mouse') return;
    this.#swipeStart = { x: event.clientX, y: event.clientY };
  };

  #onPointerUp = (/** @type {PointerEvent} */ event) => {
    if (!this.#swipeStart) return;
    const dx = event.clientX - this.#swipeStart.x;
    const dy = event.clientY - this.#swipeStart.y;
    this.#swipeStart = null;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) this.next();
    else this.previous();
  };

  #onPointerCancel = () => {
    this.#swipeStart = null;
  };
}

if (!customElements.get('ncc-lightbox')) {
  customElements.define('ncc-lightbox', NccLightbox);
}
